import { useEffect, useMemo, useState } from 'react';
import { marketingWebAPI } from '../../api/api';
import {
  RefreshCw,
  Euro,
  Eye,
  MousePointerClick,
  Target,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const RANGOS = [
  { label: 'Últimos 7 días', dias: 7 },
  { label: 'Últimos 30 días', dias: 30 },
  { label: 'Últimos 90 días', dias: 90 },
  { label: 'Últimos 12 meses', dias: 365 },
];

const fmtNum = (n) => (n ?? 0).toLocaleString('es-ES');
const fmtEur = (n) => {
  if (n == null) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n);
};
const fmtPct = (v) => (v == null ? '—' : `${(v * 100).toFixed(2)}%`);

function KpiCard({ label, value, icon: Icon, hint }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wide font-semibold text-gray-500">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function EstadoBadge({ estado }) {
  const map = {
    ENABLED: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Activa' },
    PAUSED: { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Pausada' },
    REMOVED: { cls: 'bg-gray-100 text-gray-500 border-gray-200', label: 'Eliminada' },
    UNKNOWN: { cls: 'bg-gray-100 text-gray-500 border-gray-200', label: '—' },
  };
  const cfg = map[estado] || map.UNKNOWN;
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold border rounded ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export default function MarketingWebAds() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangoDias, setRangoDias] = useState(30);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  // Conversiones offline (OCI)
  const [oci, setOci] = useState(null);
  const [ociMsg, setOciMsg] = useState(null);
  const [ociBusy, setOciBusy] = useState(false);

  const cargarOci = async () => {
    try {
      const res = await marketingWebAPI.adsConversionesEstado();
      setOci(res.data);
    } catch (e) {
      setOci(null);
    }
  };

  useEffect(() => { cargarOci(); }, []);

  const probarOci = async (dryRun) => {
    setOciBusy(true);
    setOciMsg(null);
    try {
      const res = await marketingWebAPI.adsConversionesSubir(dryRun);
      const d = res.data;
      if (dryRun) {
        setOciMsg(`Previsualización: ${d.candidatos} conversión(es) lista(s) para subir, suma ${d.suma_valor_eur} €.`);
      } else {
        setOciMsg(`Subidas: ${d.subidos} · errores: ${d.errores} · suma ${d.suma_valor_eur} €.`);
      }
      cargarOci();
    } catch (e) {
      setOciMsg(e?.response?.data?.detail || 'Error al procesar conversiones offline.');
    } finally {
      setOciBusy(false);
    }
  };

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingWebAPI.ads({ dias: rangoDias });
      setData(res.data);
    } catch (e) {
      console.error('Error Ads:', e);
      setError(e?.response?.data?.detail || 'Error cargando Google Ads');
    } finally {
      setLoading(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await marketingWebAPI.adsSync(7);
      const r = res.data || {};
      // Si el endpoint devuelve ok:false o todos los días fallaron, surface al usuario.
      // Sin esto, el "200 OK" hacía pensar que todo iba bien aunque la API rechazara cada día.
      if (r.ok === false || (r.dias_sincronizados === 0 && r.dias_fallidos > 0)) {
        const accion = {
          token_expired:
            'El refresh_token de Google Ads ha caducado o ha sido revocado. Regenéralo en local con `python web/.seo-research/scripts/ads_oauth.py`, copia el campo `refresh_token` del JSON resultante a `GOOGLE_ADS_REFRESH_TOKEN` en `.env` de producción (Emergent) y redeploya.',
          developer_token_problem:
            'Problema con GOOGLE_ADS_DEVELOPER_TOKEN. Verifica que sigue aprobado en API Center y que coincide en .env.',
          permission_denied:
            'Google Ads ha rechazado por permisos. Verifica GOOGLE_ADS_LOGIN_CUSTOMER_ID (MCC) y GOOGLE_ADS_CUSTOMER_ID (cuenta cliente), y que el MCC tiene acceso a esa cuenta.',
        }[r.error_type];
        const detalle = r.first_error_msg ? ` (detalle: ${String(r.first_error_msg).slice(0, 180)})` : '';
        setError(
          (accion || 'Sync sin días procesados.') +
            ` Intentados ${r.dias_intentados ?? '?'}, sincronizados ${r.dias_sincronizados ?? 0}, fallidos ${r.dias_fallidos ?? 0}.` +
            detalle
        );
      } else if (r.dias_fallidos > 0) {
        // Hubo algunos OK y otros fallaron — warning, pero refrescamos lo nuevo.
        setError(
          `Sync parcial: ${r.dias_sincronizados}/${r.dias_intentados} días OK, ${r.dias_fallidos} fallaron. ${r.first_error_msg ? 'Primer fallo: ' + String(r.first_error_msg).slice(0, 180) : ''}`
        );
      }
      await cargar();
    } catch (e) {
      console.error('Error sincronizando Ads:', e);
      setError(e?.response?.data?.detail || 'Error sincronizando Google Ads');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangoDias]);

  const lineChart = useMemo(() => {
    if (!data?.serie?.length) return null;
    return {
      labels: data.serie.map((d) => d.fecha),
      datasets: [
        {
          label: 'Coste (EUR)',
          data: data.serie.map((d) => d.cost_eur || 0),
          borderColor: '#1A9169',
          backgroundColor: 'rgba(26, 145, 105, 0.12)',
          fill: true,
          tension: 0.3,
          yAxisID: 'y',
        },
        {
          label: 'Clicks',
          data: data.serie.map((d) => d.clicks || 0),
          borderColor: '#D7977E',
          backgroundColor: 'rgba(215, 151, 126, 0.10)',
          fill: false,
          tension: 0.3,
          yAxisID: 'y1',
        },
        {
          label: 'Conversiones',
          data: data.serie.map((d) => d.conversions || 0),
          borderColor: '#6CA9AE',
          backgroundColor: 'rgba(108, 169, 174, 0.15)',
          fill: false,
          tension: 0.3,
          yAxisID: 'y1',
        },
      ],
    };
  }, [data]);

  const filteredCampaigns = useMemo(() => {
    if (!data?.top_campaigns) return [];
    if (!q) return data.top_campaigns;
    return data.top_campaigns.filter((c) => (c.nombre || '').toLowerCase().includes(q.toLowerCase()));
  }, [data, q]);

  // Estado de "no configurado": render dedicado con CTA a configurar env vars
  if (data && data.configured === false) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        <h1 className="text-2xl font-bold text-duetto-primary mb-2">Google Ads</h1>
        <p className="text-sm text-gray-500 mb-6">
          Métricas de campañas de duettocatering.com
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 flex gap-4">
          <AlertTriangle className="w-8 h-8 text-amber-500 shrink-0" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold mb-2">Google Ads aún no está configurado.</p>
            <p className="mb-2">
              Faltan variables de entorno en el backend. Añade en Emergent:
            </p>
            <ul className="list-disc ml-5 font-mono text-xs space-y-0.5">
              <li>GOOGLE_ADS_DEVELOPER_TOKEN</li>
              <li>GOOGLE_ADS_LOGIN_CUSTOMER_ID (Customer ID del MCC, sin guiones)</li>
              <li>GOOGLE_ADS_CUSTOMER_ID (Customer ID de Duetto, sin guiones)</li>
              <li>GOOGLE_ADS_CLIENT_ID</li>
              <li>GOOGLE_ADS_CLIENT_SECRET</li>
              <li>GOOGLE_ADS_REFRESH_TOKEN</li>
            </ul>
            <p className="mt-3 text-xs">
              Tras configurarlas, redeploya y vuelve aquí para sincronizar.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const totales = data?.totales;

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-duetto-primary">Google Ads</h1>
          <p className="text-sm text-gray-500 mt-1">
            Campañas de <span className="font-mono">duettocatering.com</span>{' '}
            {data && (
              <span>· {data.desde} → {data.hasta}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <select
            value={rangoDias}
            onChange={(e) => setRangoDias(Number(e.target.value))}
            className="border rounded px-2 py-1.5 bg-white"
          >
            {RANGOS.map((r) => (
              <option key={r.dias} value={r.dias}>{r.label}</option>
            ))}
          </select>
          <button
            onClick={sync}
            disabled={syncing}
            className="inline-flex items-center gap-1 px-3 py-1.5 border rounded bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando…' : 'Sincronizar'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4">
          {error}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <KpiCard label="Inversión" value={fmtEur(totales?.cost_eur)} icon={Euro} />
        <KpiCard label="Impresiones" value={fmtNum(totales?.impressions)} icon={Eye} />
        <KpiCard label="Clicks" value={fmtNum(totales?.clicks)} icon={MousePointerClick} hint={fmtPct(totales?.ctr) + ' CTR'} />
        <KpiCard label="Conversiones" value={fmtNum(totales?.conversions)} icon={Target} hint={fmtPct(totales?.conversion_rate) + ' tasa'} />
        <KpiCard label="CPC medio" value={fmtEur(totales?.avg_cpc_eur)} icon={TrendingUp} />
        <KpiCard label="CPA" value={fmtEur(totales?.cost_per_conversion_eur)} icon={Target} hint="Coste / conv." />
      </div>

      {/* Gráfica diaria */}
      <div className="bg-white border rounded-lg p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Evolución diaria</h3>
        {loading ? (
          <p className="text-sm text-gray-500">Cargando…</p>
        ) : lineChart ? (
          <div style={{ height: 280 }}>
            <Line
              data={lineChart}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                scales: {
                  y: { type: 'linear', position: 'left', title: { display: true, text: 'EUR' } },
                  y1: { type: 'linear', position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Clicks / Conv.' } },
                },
              }}
            />
          </div>
        ) : (
          <p className="text-sm text-gray-500">Sin datos en el rango seleccionado.</p>
        )}
      </div>

      {/* Tabla de campañas */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold text-gray-700">Campañas en el rango</h3>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar campaña…"
            className="border rounded px-2 py-1 text-sm"
          />
        </div>
        {loading ? (
          <p className="text-sm text-gray-500 p-4">Cargando…</p>
        ) : filteredCampaigns.length === 0 ? (
          <p className="text-sm text-gray-500 p-4">Sin campañas en el rango.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaña</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Coste</TableHead>
                <TableHead className="text-right">Impr.</TableHead>
                <TableHead className="text-right">Clicks</TableHead>
                <TableHead className="text-right">CTR</TableHead>
                <TableHead className="text-right">Conv.</TableHead>
                <TableHead className="text-right">CPC</TableHead>
                <TableHead className="text-right">CPA</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCampaigns.map((c) => (
                <TableRow key={c.campaign_id}>
                  <TableCell className="font-medium">{c.nombre}</TableCell>
                  <TableCell><EstadoBadge estado={c.estado} /></TableCell>
                  <TableCell className="text-right font-mono">{fmtEur(c.cost_eur)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(c.impressions)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(c.clicks)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtPct(c.ctr)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(c.conversions)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtEur(c.avg_cpc_eur)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtEur(c.cost_per_conversion_eur)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Valores de conversión y cómo se calculan */}
      <div className="bg-white border rounded-lg p-5 mt-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Valores de conversión configurados</h3>
        <p className="text-xs text-gray-500 mb-4">
          Valor estimado que se asigna a cada conversión en Google Ads. Sirve para medir el retorno
          real (ROAS) de la inversión publicitaria.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { n: 'Formulario', v: '270 €' },
            { n: 'Llamada', v: '270 €' },
            { n: 'WhatsApp', v: '270 €' },
            { n: 'Descarga catálogo', v: '90 €' },
          ].map((c) => (
            <div key={c.n} className="border rounded-lg p-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-gray-500">{c.n}</div>
              <div className="text-xl font-bold text-gray-900 mt-1">{c.v}</div>
            </div>
          ))}
        </div>

        <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-700">
          <div className="font-semibold text-gray-800 mb-2">Cómo se calcula el valor de un lead</div>
          <p className="mb-2">
            No todo lead acaba en evento. El valor estimado es el ticket medio multiplicado por la
            probabilidad de que ese lead acabe siendo un evento confirmado:
          </p>
          <div className="font-mono text-xs bg-white border rounded px-3 py-2 mb-3">
            valor por lead = ticket medio × conversión (lead→presupuesto) × aceptación (presupuesto→confirmado)
          </div>
          <ul className="space-y-1 text-xs">
            <li><strong>Ticket medio:</strong> 1.794 € (media de presupuestos confirmados 2025-2026).</li>
            <li><strong>Conversión lead → presupuesto: 25%</strong> (conservador: ~1 de cada 4 leads pide presupuesto; el resto solo mira o pide el catálogo y no vuelve).</li>
            <li><strong>Aceptación presupuesto → confirmado: 60%</strong> (real, sacado del histórico de presupuestos).</li>
          </ul>
          <div className="font-mono text-xs bg-white border rounded px-3 py-2 mt-3">
            1.794 € × 0,25 × 0,60 ≈ <strong>270 € por lead de contacto</strong> · catálogo ≈ 90 € (lead más frío, 1/3)
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Nota: la campaña puja con "Maximizar conversiones" (optimiza a número de conversiones, no a valor),
            así que estos importes hoy sirven para el informe, no para la puja. Para que el valor mueva la puja
            hay que importar el valor real de cada evento desde el ERP y cambiar a "Maximizar valor de conversión".
          </p>
        </div>
      </div>

      {/* Conversiones offline (valor real del evento confirmado → Google Ads) */}
      <div className="bg-white border rounded-lg p-5 mt-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-gray-700">Conversiones offline (valor real del evento)</h3>
          <div className="flex gap-2">
            <button onClick={() => probarOci(true)} disabled={ociBusy}
              className="px-3 py-1.5 text-xs border rounded hover:bg-gray-50 disabled:opacity-50">
              Probar (dry-run)
            </button>
            <button onClick={() => probarOci(false)} disabled={ociBusy}
              className="px-3 py-1.5 text-xs bg-duetto-primary text-white rounded hover:opacity-90 disabled:opacity-50">
              Subir ahora
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Cuando un presupuesto se confirma, se casa con su lead (por email o teléfono) y el importe real
          del evento (sin IVA) se sube a Google Ads atado al clic del anuncio. Se sube solo cada noche (04:15);
          aquí puedes forzarlo o previsualizar.
        </p>
        {oci && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border rounded-lg p-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-gray-500">Pendientes</div>
              <div className="text-xl font-bold text-gray-900 mt-1">{oci.pendientes}</div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-gray-500">Subidas</div>
              <div className="text-xl font-bold text-emerald-700 mt-1">{oci.subidas}</div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-gray-500">Errores</div>
              <div className={`text-xl font-bold mt-1 ${oci.errores > 0 ? 'text-red-600' : 'text-gray-900'}`}>{oci.errores}</div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-gray-500">Valor subido</div>
              <div className="text-xl font-bold text-gray-900 mt-1">{fmtEur(oci.suma_valor_subido_eur)}</div>
            </div>
          </div>
        )}
        {oci && !oci.configurado && (
          <p className="text-xs text-amber-700 mt-3">
            Faltan las variables GOOGLE_ADS_CONV_EVENTO_FORMULARIO / _CATALOGO en el .env del backend.
          </p>
        )}
        {ociMsg && <p className="text-xs text-gray-700 mt-3 bg-gray-50 border rounded px-3 py-2">{ociMsg}</p>}
      </div>
    </div>
  );
}
